<template>
  <highcharts
    ref="highchart"
    :options="columnChartOptions"
    :class="{'highcharts-custom--disable-drilldown': !enableDrilldown, 'highcharts-custom--disable-drillup': chartDrilleddownTo === -1}"
  />
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash';

import chartMixinFactory from '../mixins/chartMixinFactory';
import { defaultColumnChartOptions, useYAxisForDataType } from '../utils/columnChart';

// TODO: Make the logic for calls/revenue such that the stacking makes sense, 
// the individual counts adds up to the total

// These parts should be mentioned in parameters (the ones shown in the ReportingChartContainer)
// check mockCallParameters.js for example
// revenue = profit + cost + payout
// incoming = error + noConnect + connected + noTarget

export default {
  name: 'ReportingColumnChart',
  mixins: [chartMixinFactory('column')],
  computed: {
    defaultTitle() {
      return `${this.groupBy.displayName} Insights`;
    },
    columnChartOptions() {
      if (this.drilleddownTo.length) {
        return {};
      }
      
      let options = cloneDeep(defaultColumnChartOptions);
      options.title.text = this.defaultTitle;
      options.chart = {
        events: {
          drilldown: this.onDrilldown,
          drillupall: this.onDrillup
        },
        alignThresholds: true,
      };

      if (!this.groupBy || !this.data || !this.selectedParameters) {
        return options;
      }

      for (const parameter of this.selectedParameters) {
        if (parameter.stackWithParameters && parameter.stackWithParameters.length) {
          options.series = [...options.series, ...getSeries(parameter.stackWithParameters, this.data, this.groupBy.field, true, parameter)];
        } else {
          options.series = [...options.series, ...getSeries([parameter], this.data, this.groupBy.field, true)];
        }
      }

      return options;
    },
  },
  watch: {
    drilleddownData(newValue, oldValue) {
      if (newValue && !isEmpty(newValue)) {
        this.setDrilldownData(newValue, oldValue);
      }
    },
  },
  methods: {
    setDrilldownData(drilleddownData, oldDrilleddownData) {
      if (this.chartDrilleddownTo === this.currentDrilldownLevel && oldDrilleddownData && oldDrilleddownData[this.currentDrilldownItem.parentEntityId]) {
        return this.updateDrilleddownData(drilleddownData[this.currentDrilldownItem.parentEntityId].data);
      }

      let aggregatedData = drilleddownData[this.currentDrilldownItem.parentEntityId].data;
      let chartPoint = drilleddownData[this.currentDrilldownItem.parentEntityId].point;
      let drilldownToType = this.chartDrilldownPath[this.currentDrilldownLevel];
      let series = [];
      let chart = this.$refs['highchart'].chart;

      for (const parameter of this.selectedParameters) {
        if (parameter.stackWithParameters && parameter.stackWithParameters.length) {
          series = [...series, ...getSeries(parameter.stackWithParameters, aggregatedData, drilldownToType.field, true, parameter, chartPoint.name )];
        } else {
          series = [...series, ...getSeries([parameter], aggregatedData, drilldownToType.field, true, null, chartPoint.name)];
        }
      }

      for (const seriesData of series) {
        chart.addSingleSeriesAsDrilldown(chartPoint, seriesData);
      }

      chart.applyDrilldown();

      this.chartDrilleddownTo++;
    },
    updateDrilleddownData(updatedData) {
      let chart = this.$refs['highchart'].chart;
      let drilldownToType = this.chartDrilldownPath[this.currentDrilldownLevel];

      let series = [];

      for (const parameter of this.selectedParameters) {
        if (parameter.stackWithParameters && parameter.stackWithParameters.length) {
          series = [...series, ...getSeries(parameter.stackWithParameters, updatedData, drilldownToType.field, true, parameter, this.currentDrilldownItem.parentEntityId )];
        } else {
          series = [...series, ...getSeries([parameter], updatedData, drilldownToType.field, true, null, this.currentDrilldownItem.parentEntityId)];
        }
      }

      chart.series.forEach(s => {
        s.setData(getSeriesData({ name: s.options.customRingbaParameter.name }, updatedData, drilldownToType.field, true));
      });
    }
  },

};

function* getSeries(params, data, groupBy, enableDrilldown, stackOfParameter, seriesId) {
  for (const parameter of params) {
    if (groupBy !== parameter.name) {
      yield {
        id: seriesId,
        yAxis: useYAxisForDataType[parameter.dataType].axisName,
        tooltip: useYAxisForDataType[parameter.dataType].tooltip,
        name: parameter.title,
        stack: parameter.stackName || parameter.name,
        type: 'column',
        data: getSeriesData(parameter, data, groupBy, enableDrilldown),
        color: parameter.color,
        borderColor: 'transparent',
        customRingbaParameter: parameter
      };
    }
  }
}

function getSeriesData(param, data, groupBy, enableDrilldown) {
  let seriesData = [];

  for (const entityData of data) {
    seriesData.push({
      y: entityData[param.name],
      name: entityData[groupBy],
      id: entityData[groupBy],
      drilldown: !!enableDrilldown
    });
  }

  return seriesData;
}
</script>

<style lang="scss">
</style>